import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { ProjectAppTypes, useDebounce, useName } from '@bechtel/client-common';
import { GET_CREW_MEMBERS } from '@bechtel/client-gql';
import {
  Avatar,
  AvatarSize,
  Box,
  Button,
  ButtonSize,
  Combobox,
  FormControl,
  Icon,
  IconSize,
  Text,
  TextFieldSize,
  Theme,
} from '@bechtel/gds';
import { CrewMember, CrewMemberList, PagingInput, PersonaType, SearchCrewMemberInput } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useField } from 'formik';
import { get } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { AddCrewIconStyled } from './styled';

export const CrewMemberItem = ({ item }: any) => {
  const { i18n } = useLingui();
  const { getFullName } = useName();
  const userName = !item?.firstName ? t`Unassigned` : getFullName(item.firstName, item.lastName);
  return (
    <Box alignItems={{ sm: 'center' }}>
      {item?.imageUrl ? (
        <Avatar src={item.imageUrl} size={AvatarSize.MEDIUM} />
      ) : (
        <AddCrewIconStyled>
          <Icon fillColor="bechtel.blue.800" size={IconSize.MEDIUM} icon="user-plus" />
        </AddCrewIconStyled>
      )}
      <Box ml={{ sm: '300' }} flexDirection={{ sm: 'column' }}>
        <Text size={!item?.id ? '400' : '300'} weight={item?.id ? '600' : '000'} color="grey.1000">
          {userName}
        </Text>
      </Box>
    </Box>
  );
};

export interface WeldCrewMemberSelectorProps {
  fieldName: string;
  formLabel: string;
  onSelect?: (user: CrewMember) => void;
}

export const WeldCrewMemberSelector: React.FC<WeldCrewMemberSelectorProps> = ({ fieldName, formLabel, onSelect }) => {
  const { i18n } = useLingui();
  const [field, meta, helpers] = useField(fieldName);
  const { setValue } = helpers;
  const { getFullName } = useName();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText?.trim(), 500);
  const { fontFamilies } = useContext<Theme>(ThemeContext);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const [getCrewMembers, { data, error, loading, refetch, networkStatus }] = useLazyQuery<
    { crewMembers: CrewMemberList },
    { input: SearchCrewMemberInput; paging: PagingInput }
  >(GET_CREW_MEMBERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });
  useEffect(() => {
    if (debouncedSearchText?.length > 0 && debouncedSearchText?.length < 3) return;
    getCrewMembers({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
          persona: PersonaType.FOREMAN,
          appTypeId: ProjectAppTypes.AS,
        },
        paging: {
          cursor: '1',
          limit: 30,
        },
      },
    });
  }, [debouncedSearchText, getCrewMembers]);

  const crewMembersList = get(data, 'crewMembers.items', []) as CrewMemberList['items'];

  return (
    <>
      {!loading && error && (
        <Box px={{ sm: '200' }} css="height: 100%;" data-testid="retry">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
              <Text>
                <Trans>There was a problem loading welding foremen.Please click the button below to try again.</Trans>
              </Text>
            </Box>
            <Box>
              <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {!error && (
        <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
          <FormControl label={formLabel ? i18n._(formLabel) : ''} css={{ fontFamily: fontFamilies['graphik'] }}>
            <Combobox
              {...field}
              size={TextFieldSize.LARGE}
              items={crewMembersList}
              itemComponent={CrewMemberItem}
              selectedComponent={CrewMemberItem}
              term={searchText}
              setTerm={setSearchText}
              placeholder={t`Search Weld Foreman`}
              itemString={itemToString}
              selectedItem={field?.value ? field.value : t`Unassigned`}
              onSelect={(item: any) => {
                if (!item) return;
                setValue(item);
                if (!onSelect) return;
                onSelect(item);
              }}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSearchText(event.target.value);
              }}
              loading={loading || networkStatus === NetworkStatus.refetch}
              restorePreviousOnSkip
              messageOnNoResults={t`No results found`}
            />
          </FormControl>
          {meta.touched && meta.error ? (
            <Box pt={{ sm: '200' }}>
              <Text size="300" color="bechtel.red">
                {i18n._(meta.error)}
              </Text>
            </Box>
          ) : null}
        </Box>
      )}
    </>
  );
};
